import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from "@mui/material";
import { MdSwapVert } from "react-icons/md";
import { CalcOption, CalcTypeText } from "../../../../types/budget/budget";
import {
  CALC_OPTION_SHORT_LABEL,
  COLORS,
  ChoiceCard,
  ConsequenceList,
  Highlight,
  ModalCloseBar,
  ModalHero,
  SectionLabel,
  SuccessStep,
  SummaryBlock,
  SummaryRow,
  WarningBox,
  WarningHero,
  cancelButtonSx,
  modalActionsSx,
  primaryButtonSx,
} from "../CategoryMoveModals/parts";

type Step = "select" | "confirm" | "done";

type Props = {
  open: boolean;
  onClose: () => void;
  categoryLabel: string;
  supplierLabel: string;
  currentCalcOption: CalcOption;
  /** Resolve `true` quando a movimentação foi aplicada e salva. */
  onConfirm: (target: CalcOption) => Promise<boolean>;
};

const CALC_OPTIONS: CalcOption[] = ["A", "B", "C", "D"];

const CONSEQUENCES = [
  "Todos os serviços, valores, taxas, impostos, observações e configurações da categoria serão mantidos.",
  "Os totais das duas formas de cobrança e do orçamento serão recalculados.",
  "A ação será registrada na auditoria.",
];

/** Texto completo do enum sem o prefixo "[X] ", que já vai no título do card. */
const describeCalcOption = (option: CalcOption) =>
  CalcTypeText[option].replace(/^\[[A-D]\]\s*/, "");

const MoveCategoryBillingModal: React.FC<Props> = ({
  open,
  onClose,
  categoryLabel,
  supplierLabel,
  currentCalcOption,
  onConfirm,
}: Props) => {
  const [step, setStep] = useState<Step>("select");
  const [target, setTarget] = useState<CalcOption | "">("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("select");
      setTarget("");
      setSaving(false);
    }
  }, [open]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!target) return;
    setSaving(true);
    const ok = await onConfirm(target);
    setSaving(false);
    // Em caso de falha o handler já notificou o usuário; mantém o passo de
    // confirmação aberto para ele poder tentar de novo.
    if (ok) setStep("done");
  };

  const currentLabel = CALC_OPTION_SHORT_LABEL[currentCalcOption];
  const targetLabel = target ? CALC_OPTION_SHORT_LABEL[target] : "";

  // Não é exibido: nomeia a janela para leitores de tela (aria-label).
  const title =
    step === "done"
      ? "Movimentação realizada"
      : step === "confirm"
      ? "Confirmar movimentação"
      : "Mover categoria para outra forma de cobrança";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-label={title}
    >
      <ModalCloseBar onClose={handleClose} />

      {/* `dividers` desenha borda em cima e embaixo; a de cima separava o
          "fechar" do conteúdo e foi removida. */}
      <DialogContent dividers sx={{ px: 3, py: 2.5, borderTop: "none" }}>
        {step === "done" && (
          <SuccessStep
            message={
              <>
                Categoria <Highlight>{categoryLabel}</Highlight> movida com
                sucesso para a forma de cobrança{" "}
                <Highlight>{targetLabel}</Highlight>.
              </>
            }
            detail="Os totais do orçamento foram atualizados. A ação foi registrada na auditoria."
          />
        )}

        {step === "confirm" && (
          <>
            <WarningHero>
              Deseja mover a categoria <Highlight>{categoryLabel}</Highlight> do
              fornecedor <Highlight>{supplierLabel}</Highlight> da forma de
              cobrança <Highlight>{currentLabel}</Highlight> para{" "}
              <Highlight>{targetLabel}</Highlight>?
            </WarningHero>

            <WarningBox>
              O fornecedor <Highlight>{supplierLabel}</Highlight> será
              cadastrado na forma de cobrança{" "}
              <Highlight>{targetLabel}</Highlight> caso ainda não esteja. Ele
              permanece no orçamento e, se ficar sem categorias na forma de
              cobrança atual, poderá ser removido manualmente depois.
            </WarningBox>

            <ConsequenceList items={CONSEQUENCES} />
          </>
        )}

        {step === "select" && (
          <>
            <ModalHero icon={<MdSwapVert size={26} />}>
              Mova esta categoria para outra forma de cobrança, mantendo o mesmo
              fornecedor.
            </ModalHero>

            <SummaryBlock>
              <SummaryRow label="Categoria" value={categoryLabel} />
              <SummaryRow label="Fornecedor" value={supplierLabel} />
              <SummaryRow label="Forma de cobrança" value={currentLabel} />
            </SummaryBlock>

            <SectionLabel
              text="Escolha a forma de cobrança de destino"
              hint="A categoria e todos os seus serviços passam a ser calculados pelas regras da forma de cobrança escolhida."
            />

            {CALC_OPTIONS.map((option) => (
              <ChoiceCard
                key={option}
                selected={option === target}
                disabled={option === currentCalcOption}
                title={CALC_OPTION_SHORT_LABEL[option]}
                description={describeCalcOption(option)}
                onSelect={() => setTarget(option)}
              />
            ))}

            <Typography sx={{ fontSize: 12.5, color: COLORS.muted, mt: 1 }}>
              Não é permitido mover para a mesma forma de cobrança atual.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={modalActionsSx}>
        {step === "select" && (
          <>
            <Button variant="outlined" onClick={handleClose} sx={cancelButtonSx}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!target}
              onClick={() => setStep("confirm")}
              sx={primaryButtonSx}
            >
              Avançar
            </Button>
          </>
        )}
        {step === "confirm" && (
          <>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={saving}
              sx={cancelButtonSx}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={saving}
              sx={primaryButtonSx}
            >
              {saving ? "Movendo..." : "Mover categoria"}
            </Button>
          </>
        )}
        {step === "done" && (
          <Button variant="contained" onClick={onClose} sx={primaryButtonSx}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MoveCategoryBillingModal;
